import React, { useState, useEffect, useContext, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  UtensilsCrossed,
  Search,
  Clock,
  RefreshCw,
  Users,
  Split,
  ArrowRight,
  IndianRupee,
  X,
  Loader2,
  AlertTriangle,
  Eye,
  Printer,
  History,
  Menu,
  UserPlus,
  ConciergeBell,
  ChevronDown,
  Filter,
  SlidersHorizontal,
  CreditCard,
  Bell,
  Building2,
} from 'lucide-react';
import TableCard from '../../components/POS/Table/TableCard';
import { CartContext } from '../../context/CartContext';
import Header from '../../components/shared/Header';
import { useTables } from '../../hooks/useTables';
import { useAuth } from '../../context/AuthContext';
import { useTableSplits } from '../../hooks/useTableSplits';
import { toast } from 'react-hot-toast';
import ViewOrdersModal from '../../components/POS/Modals/ViewOrdersModal';
import ReceiptDisplay from '../../components/POS/Receipt/ReceiptDisplay';
import PrintReceiptModal from '../../components/POS/Receipt/PrintReceiptModal';
import RecentOrdersModal from '../../components/POS/Modals/RecentOrdersModal';
import QuickReserveModal from '../../components/POS/Modals/QuickReserveModal';
import WaitingListModal from '../../components/POS/Modals/WaitingListModal';
import TableMergeModal from '../../components/POS/Table/TableMergeModal';
import reservationService from '../../services/reservationService';
import { userService } from '../../services/userService';
import tableService from '../../services/tableService';
import { COUNTER_SECTION_NAME, COUNTER_TABLE_NAME } from '../../config/counterConfig';
import { useShift } from '../../context/ShiftContext';
import ShiftGate from '../../components/POS/Shift/ShiftGate';
import EndShiftModal from '../../components/POS/Shift/EndShiftModal';
import DayCloseModal from '../../components/POS/DayClose/DayCloseModal';
import IncomingOrdersModal from '../../components/POS/Modals/IncomingOrdersModal';
import useQROrders from '../../hooks/useQROrders';
import { P } from '../../config/permissionCodes';
import TableSelectionPageDesign01 from './Tableselection_design_01';
import useBranch from '../../hooks/useBranch';

/**
 * TableSelectionPage Component
 *
 * ✅ REFACTORED: Now uses API data directly for table status and running orders
 *
 * Data Flow:
 * 1. Fetches tables from API via useTables hook (api/admin/tables)
 * 2. API response includes: table info, splits, and running orders with amounts
 * 3. No localStorage override - displays real-time server data
 * 4. CartContext kept only for time formatting and legacy calculations
 *
 * Permissions used (all under P.POS.DINE_IN.*):
 * - CREATE      : start a new order on an available table
 * - EDIT        : select / open an existing occupied table
 * - SPLIT       : create a new split on an occupied table
 * - MERGE       : open the merge-tables modal
 * - PRINT_BILL  : show the print-bill button
 * - SETTLE      : show the settle-payment button
 * - REPRINT     : show reprint actions (passed through to TableCard)
 * - WAITLIST.*  : waiting-list add / remove / notify
 * - INCOMING.*  : incoming QR-orders modal
 */
export default function TableSelectionPage() {
  const { posDesignTheme, loading: branchLoading } = useBranch();
  const isNewDesign = Number(posDesignTheme) === 2;

  if (branchLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <Loader2 size={36} className="animate-spin text-slate-400 mx-auto mb-3" />
          <p className="text-sm text-slate-600">Loading POS layout...</p>
        </div>
      </div>
    );
  }

  if (isNewDesign) {
    return <TableSelectionPageDesign01 />;
  }

  return <LegacyTableSelectionPage />;
}

function LegacyTableSelectionPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isOwner = user?.userType === 'SUPER_USER';
  const {
    getElapsedTime,
    calculateOrderTotal,
  } = useContext(CartContext);

  // ─── Fetch tables from API ────────────────────────────────────────────────
  const {
    tables: apiTables,
    loading: tablesLoading,
    error: tablesError,
    usingFallback,
    refreshTables,
    fetchTables,
  } = useTables();

  const [currentTime, setCurrentTime] = useState(
    new Date().toLocaleTimeString('en-IN')
  );
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSection, setSelectedSection] = useState('All');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedBranchId, setSelectedBranchId] = useState(null);
  const { createSplitForTable } = useTableSplits();

  // Split Selection Modal States
  const [showSplitSelectModal, setShowSplitSelectModal] = useState(false);
  const [selectedSplits, setSelectedSplits] = useState([]);
  const [selectedTable, setSelectedTable] = useState(null);

  // PAX Input Modal States
  const [showPaxModal, setShowPaxModal] = useState(false);
  const [paxInput, setPaxInput] = useState('');
  const [paxError, setPaxError] = useState('');
  const [isPaxSubmitting, setIsPaxSubmitting] = useState(false);

  // View Orders Modal States
  const [showViewOrdersModal, setShowViewOrdersModal] = useState(false);
  const [viewOrdersData, setViewOrdersData] = useState(null);

  // Print Receipt Modal State
  const [showPrintReceiptModal, setShowPrintReceiptModal] = useState(false);
  const [printReceiptData, setPrintReceiptData] = useState(null);

  // Receipt Display Modal State
  const [showReceiptModal, setShowReceiptModal] = useState(false);

  // Recent Orders Modal State
  const [showRecentOrdersModal, setShowRecentOrdersModal] = useState(false);

  // Quick Reserve Modal State
  const [showReserveModal, setShowReserveModal] = useState(false);
  const [reserveTable, setReserveTable] = useState(null);

  // Waiting List State
  const [showWaitingListModal, setShowWaitingListModal] = useState(false);
  const [waitingList, setWaitingList] = useState(() => {
    const saved = localStorage.getItem('pos_waitingList');
    return saved ? JSON.parse(saved) : [];
  });

  // Shift State
  const { activeShift, hasActiveShift, shiftEnabled } = useShift();
  const [showEndShiftModal, setShowEndShiftModal] = useState(false);
  const [showDayCloseModal, setShowDayCloseModal] = useState(false);

  // Merge Tables Modal State
  const [showMergeModal, setShowMergeModal] = useState(false);
  const [isMerging, setIsMerging] = useState(false);
  const [mergeInitiatingTable, setMergeInitiatingTable] = useState(null);
  const [mergeMode, setMergeMode] = useState('available');

  // Incoming Orders Modal State
  const [showIncomingOrdersModal, setShowIncomingOrdersModal] = useState(false);
  const { pendingOrders: qrPendingOrders } = useQROrders();

  // Waiter Filter State
  const [waiterFilter, setWaiterFilter] = useState('all');
  const [waiters, setWaiters] = useState([]);
  const [waitersLoading, setWaitersLoading] = useState(false);
  const [showWaiterDropdown, setShowWaiterDropdown] = useState(false);

  // Filter Bar Visibility
  const [showFilterBar, setShowFilterBar] = useState(false);

  // Advanced Filters State
  const [amountFilter, setAmountFilter] = useState('all');
  const [timeFilter, setTimeFilter] = useState('all');
  const [waiterSearchQuery, setWaiterSearchQuery] = useState('');
  const waiterDropdownRef = useRef(null);

  // ─── Permissions ──────────────────────────────────────────────────────────
  const { hasPermission } = useAuth();

  /**
   * SCREEN access: pos.dine_in
   * This is the parent permission. In many permission systems, child permissions
   * (pos.dine_in.create, pos.dine_in.edit, etc.) only work if the parent is
   * also granted. We check SCREEN here and use it as an OR fallback for CREATE
   * so that a user with pos.dine_in (screen) can always start orders even if
   * pos.dine_in.create was not explicitly granted as a separate node.
   */
  const canAccessDineIn = hasPermission(P.POS.DINE_IN.SCREEN);

  // Core dine-in permissions
  //
  // ⚠️  SCREEN fallback: canCreateOrder and canEditOrder both OR with canAccessDineIn.
  //     Reason: many setups grant the parent permission 'pos.dine_in' (SCREEN) without
  //     explicitly adding every child like 'pos.dine_in.create'. The SCREEN grant should
  //     imply the ability to use the screen's core actions (open + create orders).
  //     If you want strict child-only enforcement, remove the || canAccessDineIn parts.
  const canCreateOrder   = hasPermission(P.POS.DINE_IN.CREATE) ;
  const canEditOrder     = hasPermission(P.POS.DINE_IN.EDIT)   ;
  const canSplitOrder    = hasPermission(P.POS.DINE_IN.SPLIT);
  const canMerge         = hasPermission(P.POS.DINE_IN.MERGE);
  const canPrintBillPerm = hasPermission(P.POS.DINE_IN.PRINT_BILL);
  const canSettlePerm    = hasPermission(P.POS.DINE_IN.SETTLE);
  const canReprintPerm   = hasPermission(P.POS.DINE_IN.REPRINT);
  const canCancelOrder   = hasPermission(P.POS.DINE_IN.CANCEL);
  const canApplyPromo    = hasPermission(P.POS.DINE_IN.PROMO);
  const canApplyDiscount = hasPermission(P.POS.DINE_IN.DISCOUNT);

  // Waitlist permissions
  const canWaitlistAdd    = hasPermission(P.POS.DINE_IN.WAITLIST.ADD);
  const canWaitlistRemove = hasPermission(P.POS.DINE_IN.WAITLIST.REMOVE);
  const canWaitlistNotify = hasPermission(P.POS.DINE_IN.WAITLIST.NOTIFY);
  const canAccessWaitlist = canWaitlistAdd || canWaitlistRemove || canWaitlistNotify;

  // Incoming orders permissions
  const canViewIncoming      = hasPermission(P.POS.DINE_IN.INCOMING.VIEW);
  const canSendKitchen       = hasPermission(P.POS.DINE_IN.INCOMING.SEND_KITCHEN);
  const canPrintKOTIncoming  = hasPermission(P.POS.DINE_IN.INCOMING.PRINT_KOT);

  // ─── Effects ──────────────────────────────────────────────────────────────

  // Close waiter dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (waiterDropdownRef.current && !waiterDropdownRef.current.contains(event.target)) {
        setShowWaiterDropdown(false);
      }
    };
    if (showWaiterDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showWaiterDropdown]);

  // Update clock every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date().toLocaleTimeString('en-IN'));
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Persist waiting list to localStorage
  useEffect(() => {
    localStorage.setItem('pos_waitingList', JSON.stringify(waitingList));
  }, [waitingList]);

  // Fetch waiters for filter dropdown
  useEffect(() => {
    const fetchWaiters = async () => {
      setWaitersLoading(true);
      try {
        const response = await userService.getWaiters();
        let waiterData = [];
        if (Array.isArray(response)) {
          waiterData = response;
        } else if (Array.isArray(response?.data)) {
          waiterData = response.data;
        } else if (response?.data?.data && Array.isArray(response.data.data)) {
          waiterData = response.data.data;
        }

        const mappedWaiters = waiterData.map(u => ({
          id: u.id,
          name: u.firstName && u.lastName
            ? `${u.firstName} ${u.lastName}`.trim()
            : u.name || u.email || 'Unknown',
          active: u.active !== false,
        }));

        setWaiters(mappedWaiters.filter(w => w.active));
      } catch (err) {
        console.error('Failed to fetch waiters for filter:', err);
      } finally {
        setWaitersLoading(false);
      }
    };
    fetchWaiters();
  }, []);

  // Extract unique branches from API data
  const branches = useMemo(() => {
    if (!isOwner) return [];
    const branchMap = new Map();
    (apiTables || []).forEach(t => {
      if (t.branchId && t.branchName && !branchMap.has(t.branchId)) {
        branchMap.set(t.branchId, { id: t.branchId, name: t.branchName });
      }
    });
    const list = Array.from(branchMap.values());
    return list.length > 1 ? list : [];
  }, [apiTables, isOwner]);

  // Filter out counter-section, counter table, and inactive tables.
  // Belt-and-suspenders: section filter catches the normal case; name filter
  // catches counter tables that may have ended up in another section.
  const tables = (apiTables || []).filter(t =>
    t.isActive === true &&
    t.section?.toUpperCase() !== COUNTER_SECTION_NAME &&
    t.name?.toUpperCase() !== COUNTER_TABLE_NAME &&
    t.number?.toUpperCase() !== COUNTER_TABLE_NAME &&
    (!selectedBranchId || t.branchId === selectedBranchId)
  );

  const sections = ['All', ...new Set(
    (apiTables || [])
      .filter(t =>
        t.section?.toUpperCase() !== COUNTER_SECTION_NAME &&
        t.name?.toUpperCase() !== COUNTER_TABLE_NAME &&
        t.number?.toUpperCase() !== COUNTER_TABLE_NAME &&
        (!selectedBranchId || t.branchId === selectedBranchId)
      )
      .map(t => t.section)
      .filter(Boolean)
  )];

  // ─── Helper functions ─────────────────────────────────────────────────────

  const getTableRunningAmount = (table) => {
    return (table.allSplits || []).reduce((total, split) => {
      return total + (split.runningOrders || []).reduce((sum, order) => sum + (order.runningAmount || 0), 0);
    }, 0);
  };

  const getTableWaitingTime = (table) => {
    let oldestTime = null;
    (table.allSplits || []).forEach(split => {
      (split.runningOrders || []).forEach(order => {
        if (order.startTimestamp) {
          const orderTime = new Date(order.startTimestamp);
          if (!isNaN(orderTime.getTime()) && (!oldestTime || orderTime < oldestTime)) {
            oldestTime = orderTime;
          }
        }
      });
    });
    if (!oldestTime) return 0;
    return Math.floor((Date.now() - oldestTime.getTime()) / 60000);
  };

  const hasActiveReservation = (table) => {
    if (!Array.isArray(table.reservations)) return false;
    const now = new Date();
    return table.reservations.some(r => {
      if (r.status?.toLowerCase() !== 'confirmed') return false;
      const start = new Date(`${r.reservationDate}T${r.reservationTime}`);
      const end = new Date(start.getTime() + (r.expectedDurationMins || 0) * 60000);
      return now <= end;
    });
  };

  // ─── Filtering ────────────────────────────────────────────────────────────

  const filteredTables = tables.filter(table => {
    const searchMatch =
      table.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      table.tableNumber?.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesSection = selectedSection === 'All' || table.section === selectedSection;

    const tableStatus = (table.status || '').toLowerCase();
    const isReserved = hasActiveReservation(table);

    const matchesStatus =
      statusFilter === 'all' ||
      (statusFilter === 'available' && tableStatus === 'available' && !isReserved) ||
      (statusFilter === 'occupied' && tableStatus === 'occupied') ||
      (statusFilter === 'reserved' && isReserved);

    let matchesWaiter = true;
    if (waiterFilter !== 'all') {
      matchesWaiter = (table.allSplits || []).some(s => s.waiterId === waiterFilter);
    }

    let matchesAmount = true;
    if (amountFilter !== 'all') {
      if (table.status !== 'occupied') {
        matchesAmount = false;
      } else {
        const amount = getTableRunningAmount(table);
        switch (amountFilter) {
          case 'low':    matchesAmount = amount > 0 && amount <= 500; break;
          case 'medium': matchesAmount = amount > 500 && amount <= 2000; break;
          case 'high':   matchesAmount = amount > 2000; break;
          default:       matchesAmount = true;
        }
      }
    }

    let matchesTime = true;
    if (timeFilter !== 'all') {
      if (table.status !== 'occupied') {
        matchesTime = false;
      } else {
        const minutes = getTableWaitingTime(table);
        switch (timeFilter) {
          case 'new':     matchesTime = minutes <= 30; break;
          case 'waiting': matchesTime = minutes > 30 && minutes <= 60; break;
          case 'long':    matchesTime = minutes > 60; break;
          default:        matchesTime = true;
        }
      }
    }

    return searchMatch && matchesSection && matchesStatus && matchesWaiter && matchesAmount && matchesTime;
  });

  const activeFilterCount = [
    waiterFilter !== 'all',
    amountFilter !== 'all',
    timeFilter !== 'all',
  ].filter(Boolean).length;

  const clearAdvancedFilters = () => {
    setWaiterFilter('all');
    setAmountFilter('all');
    setTimeFilter('all');
  };

  const groupedTables = filteredTables.reduce((acc, table) => {
    const section = table.section || 'Other';
    if (!acc[section]) acc[section] = [];
    acc[section].push(table);
    return acc;
  }, {});

  const stats = useMemo(() => {
    let available = 0, occupied = 0, reserved = 0;
    tables.forEach(table => {
      if (hasActiveReservation(table)) reserved++;
      else if (table.status === 'occupied') occupied++;
      else if (table.status === 'available') available++;
    });
    return {
      total: tables.length,
      available,
      occupied,
      reserved,
      activeOrders: tables.reduce((count, t) => count + (t.runningOrders?.length || 0), 0),
    };
  }, [tables]);

  const formatRunningTime = (startTime) => {
    if (typeof getElapsedTime === 'function' && startTime) return getElapsedTime(startTime);
    if (!startTime) return '0m';
    const diff = Date.now() - new Date(startTime).getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(minutes / 60);
    return hours > 0 ? `${hours}h ${minutes % 60}m` : `${minutes}m`;
  };

  // ─── Table Selection Logic ────────────────────────────────────────────────

  const handleTableSelect = (table) => {
    // Reserved table — no action
    if (table.status === 'reserved') {
      toast.error('This table is reserved');
      return;
    }

    // Merged slave table — redirect to master
    if (table.status === 'merged') {
      const splits = table.allSplits || [];
      const mergedSplit = splits.find(s => s.status === 'merged') || splits.find(s => s.masterTableId);
      const masterName = mergedSplit?.masterTableName ||
        (mergedSplit?.masterTableId ? `T${mergedSplit.masterTableId}` : 'master table');
      toast.info(`This table is merged with ${masterName}. Place orders there.`);
      return;
    }

    const runningOrders = table.runningOrders || [];
    const allTableSplits = table.allSplits || [];
    const activeSplits = allTableSplits.filter(s =>
      s.status === 'active' || s.status === 'running' || s.status === 'payment_pending'
    );
    const heldSplits = allTableSplits.filter(s =>
      ['hold', 'HOLD', 'HELD'].includes(s.status)
    );

    const isTableEmpty = allTableSplits.length === 0 && runningOrders.length === 0;

    // ── Empty table: need CREATE permission ──────────────────────────────────
    if (isTableEmpty) {
      if (!canCreateOrder) {
        toast.error('You do not have permission to create new orders', { icon: '🚫' });
        return;
      }
      setSelectedTable(table);
      setPaxInput('');
      setPaxError('');
      setShowPaxModal(true);
      return;
    }

    // ── Occupied / held table: need EDIT permission ──────────────────────────
    if (!canEditOrder) {
      toast.error('You do not have permission to open existing orders', { icon: '🚫' });
      return;
    }

    // CASE 0: allSplits empty but runningOrders exist (legacy / partial data)
    if (allTableSplits.length === 0 && runningOrders.length > 0) {
      if (runningOrders.length === 1) {
        const order = runningOrders[0];
        navigate(`/pos/billing/${table.id}`, {
          state: { tableId: table.id, split: order.tableSplit, pax: order.pax || 2 },
        });
        return;
      }
      const splitsWithOrderData = runningOrders.map(order => ({
        tableSplit: order.tableSplit,
        splitName: `Split ${order.tableSplit}`,
        pax: order.pax || 0,
        amount: order.amount || 0,
        createdAt: order.createdAt || new Date().toISOString(),
        status: order.status || 'active',
      }));
      setSelectedSplits(splitsWithOrderData);
      setSelectedTable(table);
      setShowSplitSelectModal(true);
      return;
    }

    // CASE 1: Multiple splits → show split selection modal
    if (allTableSplits.length > 1) {
      const splitsWithOrderData = allTableSplits.map(split => {
        const runningOrder = runningOrders.find(
          order =>
            order.tableSplit === split.splitName ||
            order.tableSplit === split.splitName?.replace('Split ', '')
        );
        return {
          tableSplit: split.splitName?.replace('Split ', '') || split.splitName,
          splitName: split.splitName,
          pax: split.peopleCount || 0,
          amount: runningOrder?.amount || 0,
          createdAt: runningOrder?.createdAt || new Date().toISOString(),
          status: split.status || 'active',
        };
      });
      setSelectedSplits(splitsWithOrderData);
      setSelectedTable(table);
      setShowSplitSelectModal(true);
      return;
    }

    // CASE 2: Single active split → navigate directly
    if (activeSplits.length === 1 && allTableSplits.length === 1) {
      const split = activeSplits[0];
      navigate(`/pos/billing/${table.id}`, {
        state: {
          tableId: table.id,
          split: split.splitName?.replace('Split ', '') || split.splitName,
          pax: split.peopleCount || 2,
        },
      });
      return;
    }

    // CASE 3: Single held split → navigate directly
    if (heldSplits.length === 1 && activeSplits.length === 0 && allTableSplits.length === 1) {
      const split = heldSplits[0];
      navigate(`/pos/billing/${table.id}`, {
        state: {
          tableId: table.id,
          split: split.splitName?.replace('Split ', '') || split.splitName,
          pax: split.peopleCount || 2,
        },
      });
      return;
    }

    // Fallback: show PAX modal (should rarely hit here)
    if (canCreateOrder) {
      setSelectedTable(table);
      setPaxInput('');
      setPaxError('');
      setShowPaxModal(true);
    } else {
      toast.error('You do not have permission to create new orders', { icon: '🚫' });
    }
  };

  // ─── Split creation (PAX modal) ───────────────────────────────────────────

  /**
   * Determines whether we're creating the very first split (needs CREATE)
   * or an additional split on an occupied table (needs SPLIT).
   *
   * Pass `table` explicitly — never rely on `selectedTable` state here because
   * React state updates are async: setSelectedTable() called just before this
   * will NOT have updated the state yet within the same render/tick.
   * All callers already pass the table object directly, so this is safe.
   */
  const resolveCreateSplitPermission = (table) => {
    if (!table) return canCreateOrder; // no table data → fall back to CREATE check
    const allTableSplits = table.allSplits || [];
    const runningOrders  = table.runningOrders || [];
    const isFirstSplit   = allTableSplits.length === 0 && runningOrders.length === 0;
    // First split on a fresh table → CREATE (canCreateOrder already includes SCREEN fallback)
    if (isFirstSplit) return canCreateOrder;
    // Additional split on an occupied table → SPLIT
    return canSplitOrder;
  };

  const handlePaxSubmit = async () => {
    const pax = parseInt(paxInput);
    if (!pax || pax <= 0) {
      setPaxError('Please enter a valid number of guests');
      return;
    }
    if (!resolveCreateSplitPermission(selectedTable)) {
      toast.error('You do not have permission to create a split', { icon: '🚫' });
      return;
    }
    try {
      setIsPaxSubmitting(true);
      const newSplit = await createSplitForTable(selectedTable.id, { peopleCount: pax });
      const tableSplit = newSplit.splitName || newSplit.id;
      navigate(`/pos/billing/${selectedTable.id}`, {
        state: { tableId: selectedTable.id, split: tableSplit, pax },
      });
    } catch (error) {
      console.error('❌ Split creation failed:', error);
      toast.error('Failed to create split');
      setIsPaxSubmitting(false);
    }
  };

  const handleQuickPax = async (value) => {
    if (!resolveCreateSplitPermission(selectedTable)) {
      toast.error('You do not have permission to create a split', { icon: '🚫' });
      return;
    }
    try {
      setIsPaxSubmitting(true);
      const newSplit = await createSplitForTable(selectedTable.id, { peopleCount: value });
      const tableSplit = newSplit.splitName || newSplit.id;
      navigate(`/pos/billing/${selectedTable.id}`, {
        state: { tableId: selectedTable.id, split: tableSplit, pax: value },
      });
    } catch (error) {
      console.error('❌ Split creation failed:', error);
      toast.error('Failed to create split');
      setIsPaxSubmitting(false);
    }
  };

  const handleSelectSplit = (tableId, tableSplit, pax) => {
    if (!canEditOrder) {
      toast.error('You do not have permission to open this order', { icon: '🚫' });
      return;
    }
    setShowSplitSelectModal(false);
    navigate(`/pos/billing/${tableId}`, {
      state: { tableId, split: tableSplit, pax: pax || 2 },
    });
  };

  // ─── Modal handlers ───────────────────────────────────────────────────────

  const handleViewOrders = (tableId, tableSplit, tableName) => {
    setViewOrdersData({ tableId, tableSplit, tableName });
    setShowViewOrdersModal(true);
  };

  const handlePrintBill = (tableId, tableSplit, pax) => {
    if (!canPrintBillPerm) {
      toast.error('You do not have permission to print bills', { icon: '🚫' });
      return;
    }
    const table = apiTables.find(t => t.id === tableId);
    const tableName = table?.tableNumber || table?.name || `Table ${tableId}`;
    setPrintReceiptData({ tableId, tableSplit, tableName, pax: pax || 2 });
    setShowPrintReceiptModal(true);
  };

  const handleSettleBill = (tableId, tableSplit, pax) => {
    if (!canSettlePerm) {
      toast.error('You do not have permission to settle bills', { icon: '🚫' });
      return;
    }
    navigate(`/pos/billing/${tableId}`, {
      state: { tableId, split: tableSplit, pax: pax || 2, autoSettle: true },
    });
  };

  const getOrderTotal = (order) => {
    if (order && typeof order.amount === 'number') return order.amount;
    if (typeof calculateOrderTotal === 'function') return calculateOrderTotal(order);
    if (!order || !order.items) return 0;
    return order.items.reduce((sum, item) => sum + (item.finalPrice || item.price || 0) * item.quantity, 0);
  };

  const handleQuickReserve = (table) => {
    setReserveTable(table);
    setShowReserveModal(true);
  };

  const handleReservationSubmit = () => refreshTables();

  // ─── Waiting list ─────────────────────────────────────────────────────────

  const handleAddToWaitingList = (customer) => {
    if (!canWaitlistAdd) { toast.error('No permission to add to waiting list', { icon: '🚫' }); return; }
    setWaitingList(prev => [...prev, {
      id: Date.now(), ...customer,
      status: 'WAITING', addedAt: new Date().toISOString(),
      notified: false, notifiedAt: null,
    }]);
  };

  const handleNotifyCustomer = (customerId) => {
    if (!canWaitlistNotify) { toast.error('No permission to notify customers', { icon: '🚫' }); return; }
    setWaitingList(prev =>
      prev.map(c => c.id === customerId
        ? { ...c, notified: true, notifiedAt: new Date().toISOString() }
        : c)
    );
  };

  const handleRemoveFromWaitingList = (customerId) => {
    if (!canWaitlistRemove) { toast.error('No permission to remove from waiting list', { icon: '🚫' }); return; }
    setWaitingList(prev => prev.filter(c => c.id !== customerId));
  };

  const handleCustomerSeated = (customerId) => {
    setWaitingList(prev =>
      prev.map(c => c.id === customerId ? { ...c, status: 'SEATED' } : c)
    );
  };

  const activeWaitingCount = waitingList.filter(w => w.status === 'WAITING').length;

  // ─── Merge ────────────────────────────────────────────────────────────────

  const handleOpenMergeModal = (table, mode) => {
    if (!canMerge) { toast.error('You do not have permission to merge tables', { icon: '🚫' }); return; }
    setMergeInitiatingTable(table);
    setMergeMode(mode);
    setShowMergeModal(true);
  };

  const handleMergeTables = async (mergeData) => {
    try {
      setIsMerging(true);
      const masterTableId = mergeData.masterTableId;
      const mergedTableIds = mergeData.tableIds.filter(id => id !== masterTableId);

      if (mergeData.mode === 'expand') {
        const tableData = apiTables.find(t => t.id === masterTableId);
        const activeSplit = tableData?.allSplits?.find(s =>
          s.status === 'active' || s.status === 'running'
        );
        await tableService.addTablesToMerge({
          masterTableId,
          additionalTableIds: mergedTableIds,
          splitName: activeSplit?.splitName || 'A',
        });
        toast.success('Tables added successfully!');
        setShowMergeModal(false);
        setMergeInitiatingTable(null);
        refreshTables();
      } else {
        const paxCount = mergeData.peopleCount || 2;
        const response = await tableService.mergeTables({
          masterTableId, mergedTableIds, peopleCount: paxCount,
        });
        toast.success('Tables merged successfully!');
        setShowMergeModal(false);
        setMergeInitiatingTable(null);
        const splitName = response?.data?.splitName || response?.splitName || 'A';
        navigate(`/pos/billing/${masterTableId}`, {
          state: { tableId: masterTableId, split: splitName, pax: paxCount },
        });
      }
    } catch (error) {
      console.error('Failed to merge tables:', error);
      toast.error(error.response?.data?.message || error.message || 'Failed to merge tables');
    } finally {
      setIsMerging(false);
    }
  };

  // ─── Reservation actions ──────────────────────────────────────────────────

  const handleReservationAction = async (action, reservation, table) => {
    try {
      switch (action) {
        case 'cancel':
          await reservationService.cancel(reservation.id);
          toast.success('Reservation cancelled');
          break;
        case 'no_show':
          await reservationService.markNoShow(reservation.id);
          toast.success('Marked as no-show');
          break;
        case 'seated':
          // Check permission before starting the order flow
          if (!canCreateOrder) {
            toast.error('You do not have permission to create new orders', { icon: '🚫' });
            return;
          }
          await reservationService.markCompleted(reservation.id);
          toast.success('Reservation completed - Starting order');
          await refreshTables();
          if (table) {
            setSelectedTable(table);
            setPaxInput(String(reservation.guestCount || 2));
            setPaxError('');
            setShowPaxModal(true);
          }
          return;
        case 'completed':
          await reservationService.markCompleted(reservation.id);
          toast.success('Reservation completed');
          break;
        default:
          console.warn('Unknown reservation action:', action);
          return;
      }
      refreshTables();
    } catch (error) {
      console.error('Reservation action failed:', error);
      toast.error(error.response?.data?.message || 'Action failed');
    }
  };

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="h-screen w-screen flex flex-col bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100 overflow-hidden">
      {/* Shift Gate */}
      <ShiftGate />

      {/* Header */}
      <Header
        shift={shiftEnabled ? activeShift : null}
        onEndShift={() => setShowEndShiftModal(true)}
        onDayClose={() => setShowDayCloseModal(true)}
      />

      {/* Loading */}
      {tablesLoading && (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <Loader2 className="w-12 h-12 text-teal-600 animate-spin mx-auto mb-4" />
            <p className="text-slate-600 font-medium">Loading tables...</p>
          </div>
        </div>
      )}

      {/* Main Content */}
      {!tablesLoading && (
        <>
          {/* Offline Warning */}
          {usingFallback && (
            <div className="bg-amber-50 border-b-2 border-amber-300 px-6 py-3">
              <div className="flex items-center gap-3">
                <AlertTriangle size={20} className="text-amber-600 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-amber-900">
                    ⚠️ Offline Mode - Using Cached Data
                  </p>
                  <p className="text-xs text-amber-700">
                    Unable to connect to server. Table statuses and orders may be outdated. Changes may not be saved.
                  </p>
                </div>
                <button
                  onClick={refreshTables}
                  className="px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold rounded-lg transition flex items-center gap-1.5"
                >
                  <RefreshCw size={14} /> Retry Connection
                </button>
              </div>
            </div>
          )}

          {/* Filter Bar */}
          <div className="bg-white border-b border-slate-200 shadow-sm">
            {/* Collapsed Summary Bar */}
            <div className="px-6 py-1.5 flex items-center gap-3">
              {/* Status pills */}
              <div className="flex items-center gap-1.5">
                {[
                  { value: 'all',       label: `All ${stats.total}`,        dot: 'bg-slate-400',  active: 'bg-slate-200 text-slate-800' },
                  { value: 'available', label: `Free ${stats.available}`,   dot: 'bg-emerald-400', active: 'bg-emerald-100 text-emerald-700' },
                  { value: 'occupied',  label: `Busy ${stats.occupied}`,    dot: 'bg-indigo-400',  active: 'bg-indigo-100 text-indigo-700' },
                  { value: 'reserved',  label: `Rsv ${stats.reserved}`,     dot: 'bg-blue-400',    active: 'bg-blue-100 text-blue-700' },
                ].map(btn => (
                  <button
                    key={btn.value}
                    onClick={() => setStatusFilter(statusFilter === btn.value && btn.value !== 'all' ? 'all' : btn.value)}
                    className={`flex items-center gap-1 px-2 py-1 rounded text-[11px] font-semibold transition ${
                      statusFilter === btn.value ? btn.active : 'text-slate-500 hover:bg-slate-100'
                    }`}
                  >
                    <div className={`w-1.5 h-1.5 rounded-full ${btn.dot}`} />
                    {btn.label}
                  </button>
                ))}
              </div>

              {/* Branch dropdown */}
              {branches.length > 1 && (
                <>
                  <div className="h-4 w-px bg-slate-200" />
                  <div className="relative">
                    <Building2 size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                    <select
                      value={selectedBranchId ?? ''}
                      onChange={e => { setSelectedBranchId(e.target.value || null); setSelectedSection('All'); }}
                      className="pl-7 pr-6 py-1 text-[11px] font-semibold border border-gray-300 rounded bg-white focus:ring-2 focus:ring-teal-500 focus:border-teal-500 appearance-none cursor-pointer"
                    >
                      <option value="">All Branches</option>
                      {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                    </select>
                    <ChevronDown size={10} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                  </div>
                </>
              )}

              {/* Section quick tabs */}
              <div className="h-4 w-px bg-slate-200" />
              <div className="flex items-center gap-1">
                {sections.map(section => (
                  <button
                    key={section}
                    onClick={() => setSelectedSection(section)}
                    className={`px-2 py-1 rounded font-semibold text-[11px] transition ${
                      selectedSection === section ? 'bg-teal-600 text-white' : 'text-slate-500 hover:bg-slate-100'
                    }`}
                  >
                    {section}
                  </button>
                ))}
              </div>
              <div className="flex-1" />

              {(activeFilterCount > 0 || searchQuery) && (
                <span className="text-[10px] font-semibold text-teal-600 bg-teal-50 px-2 py-0.5 rounded-full">
                  {activeFilterCount + (searchQuery ? 1 : 0)} filter{(activeFilterCount + (searchQuery ? 1 : 0)) !== 1 ? 's' : ''} active
                </span>
              )}

              <button
                onClick={() => setShowFilterBar(!showFilterBar)}
                className={`flex items-center gap-1 px-2 py-1 rounded text-[11px] font-semibold transition border ${
                  showFilterBar ? 'bg-teal-50 text-teal-700 border-teal-300' : 'text-slate-500 border-slate-200 hover:bg-slate-100'
                }`}
              >
                <SlidersHorizontal size={12} />
                Filters
              </button>

              <button
                onClick={refreshTables}
                className="p-1.5 bg-teal-600 text-white rounded hover:bg-teal-700 transition"
                title="Refresh tables"
              >
                <RefreshCw size={14} />
              </button>
            </div>

            {/* Expanded Filter Panel */}
            {showFilterBar && (
              <div className="px-6 py-2 border-t border-slate-100">
                <div className="flex items-center gap-3">
                  {/* Search */}
                  <div className="w-56 relative">
                    <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                      type="text"
                      placeholder="Search tables..."
                      value={searchQuery}
                      onChange={e => setSearchQuery(e.target.value)}
                      className="w-full pl-8 pr-3 py-1.5 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent text-xs"
                    />
                  </div>

                  <div className="h-5 w-px bg-slate-200" />

                  {/* Waiter Filter */}
                  <div className="relative" ref={waiterDropdownRef}>
                    <button
                      onClick={() => { setShowWaiterDropdown(!showWaiterDropdown); if (!showWaiterDropdown) setWaiterSearchQuery(''); }}
                      className={`flex items-center gap-2 px-3 py-1.5 rounded-lg font-semibold text-xs transition border ${
                        waiterFilter !== 'all'
                          ? 'bg-teal-50 text-teal-700 border-teal-300'
                          : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-50'
                      }`}
                    >
                      <ConciergeBell size={14} />
                      <span className="max-w-[100px] truncate">
                        {waiterFilter === 'all' ? 'All Waiters' : waiters.find(w => w.id === waiterFilter)?.name || 'Waiter'}
                      </span>
                      <ChevronDown size={12} className={`transition-transform ${showWaiterDropdown ? 'rotate-180' : ''}`} />
                    </button>

                    {showWaiterDropdown && (
                      <div className="absolute top-full left-0 mt-1 w-56 bg-white rounded-lg shadow-xl border border-slate-200 z-50 overflow-hidden">
                        {!waitersLoading && waiters.length > 0 && (
                          <div className="p-2 border-b border-slate-100">
                            <div className="relative">
                              <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
                              <input
                                type="text"
                                placeholder="Search..."
                                value={waiterSearchQuery}
                                onChange={e => setWaiterSearchQuery(e.target.value)}
                                onClick={e => e.stopPropagation()}
                                className="w-full pl-7 pr-2 py-1.5 text-xs border border-slate-200 rounded focus:outline-none focus:ring-1 focus:ring-teal-500"
                                autoFocus
                              />
                              {waiterSearchQuery && (
                                <button
                                  onClick={e => { e.stopPropagation(); setWaiterSearchQuery(''); }}
                                  className="absolute right-1.5 top-1/2 -translate-y-1/2 p-0.5 hover:bg-slate-100 rounded"
                                >
                                  <X size={10} className="text-slate-400" />
                                </button>
                              )}
                            </div>
                          </div>
                        )}
                        <div className="max-h-48 overflow-y-auto">
                          <button
                            onClick={() => { setWaiterFilter('all'); setShowWaiterDropdown(false); setWaiterSearchQuery(''); }}
                            className={`w-full text-left px-3 py-2 text-xs transition ${waiterFilter === 'all' ? 'bg-teal-50 text-teal-700 font-semibold' : 'text-slate-700 hover:bg-slate-50'}`}
                          >
                            All Waiters
                          </button>
                          {waitersLoading ? (
                            <div className="px-3 py-2 text-center"><Loader2 size={14} className="animate-spin text-teal-500 mx-auto" /></div>
                          ) : waiters.length > 0 ? (
                            (() => {
                              const filtered = waiterSearchQuery.trim()
                                ? waiters.filter(w => w.name.toLowerCase().includes(waiterSearchQuery.toLowerCase()))
                                : waiters;
                              return filtered.length > 0 ? (
                                <>
                                  <div className="border-t border-slate-100" />
                                  {filtered.map(waiter => (
                                    <button
                                      key={waiter.id}
                                      onClick={() => { setWaiterFilter(waiter.id); setShowWaiterDropdown(false); setWaiterSearchQuery(''); }}
                                      className={`w-full text-left px-3 py-2 text-xs transition flex items-center gap-2 ${waiterFilter === waiter.id ? 'bg-teal-50 text-teal-700 font-semibold' : 'text-slate-700 hover:bg-slate-50'}`}
                                    >
                                      <div className="w-5 h-5 rounded-full bg-teal-100 text-teal-600 flex items-center justify-center text-[10px] font-bold">
                                        {waiter.name.charAt(0).toUpperCase()}
                                      </div>
                                      <span className="truncate">{waiter.name}</span>
                                    </button>
                                  ))}
                                </>
                              ) : (
                                <div className="px-3 py-3 text-xs text-slate-500 text-center">No match for "{waiterSearchQuery}"</div>
                              );
                            })()
                          ) : (
                            <div className="px-3 py-2 text-xs text-slate-500 text-center">No waiters found</div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Amount Filter */}
                  <div className="flex items-center gap-1">
                    <IndianRupee size={14} className="text-slate-400" />
                    <span className="text-xs text-slate-500 mr-1">Amount:</span>
                    {[
                      { value: 'all', label: 'All' },
                      { value: 'low', label: '<500' },
                      { value: 'medium', label: '500-2K' },
                      { value: 'high', label: '>2K' },
                    ].map(opt => (
                      <button
                        key={opt.value}
                        onClick={() => setAmountFilter(opt.value)}
                        className={`px-2 py-1 rounded text-xs font-medium transition ${amountFilter === opt.value ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>

                  {/* Time Filter */}
                  <div className="flex items-center gap-1">
                    <Clock size={14} className="text-slate-400" />
                    <span className="text-xs text-slate-500 mr-1">Time:</span>
                    {[
                      { value: 'all',     label: 'All' },
                      { value: 'new',     label: '<30m' },
                      { value: 'waiting', label: '30-60m' },
                      { value: 'long',    label: '>1hr' },
                    ].map(opt => (
                      <button
                        key={opt.value}
                        onClick={() => setTimeFilter(opt.value)}
                        className={`px-2 py-1 rounded text-xs font-medium transition ${timeFilter === opt.value ? 'bg-purple-100 text-purple-700' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>

                  {activeFilterCount > 0 && (
                    <button
                      onClick={clearAdvancedFilters}
                      className="flex items-center gap-1 px-2 py-1 text-xs text-red-600 hover:bg-red-50 rounded transition"
                    >
                      <X size={12} />
                      Clear
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Table Grid */}
          <main className="flex-1 overflow-y-auto px-4 py-3">
            {Object.keys(groupedTables).length === 0 ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <p className="text-slate-400 text-lg font-medium">No tables found</p>
                  <p className="text-slate-400 text-sm mt-2">Try adjusting your search or filters</p>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {Object.entries(groupedTables).map(([section, sectionTables]) => (
                  <div key={section}>
                    <div className="flex items-center gap-2 mb-2">
                      <h2 className="text-sm font-bold text-slate-700">{section}</h2>
                      <div className="h-px flex-1 bg-slate-200" />
                      <span className="text-[10px] font-semibold text-slate-400">{sectionTables.length}</span>
                    </div>
                    <div className="grid gap-2.5 grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 2xl:grid-cols-8">
                      {sectionTables.map(table => (
                        <TableCard
                          key={table.id}
                          table={table}
                          splits={table.allSplits || []}
                          tableOrders={table.runningOrders || []}
                          formatRunningTime={formatRunningTime}
                          onClick={() => handleTableSelect(table)}
                          calculateOrderTotal={getOrderTotal}
                          onViewOrders={handleViewOrders}
                          onPrintBill={canPrintBillPerm ? handlePrintBill : null}
                          onSettleBill={canSettlePerm ? handleSettleBill : null}
                          onReserve={handleQuickReserve}
                          onReservationAction={handleReservationAction}
                          onMerge={canMerge ? handleOpenMergeModal : null}
                          // Pass additional permission flags so TableCard can show/hide
                          // its own action buttons (reprint, cancel, etc.)
                          canReprint={canReprintPerm}
                          canCancel={canCancelOrder}
                          canApplyPromo={canApplyPromo}
                          canApplyDiscount={canApplyDiscount}
                          canSplit={canSplitOrder}
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </main>

          {/* ── PAX Input Modal ──────────────────────────────────────────── */}
          {showPaxModal && selectedTable && (
            <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
              <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 space-y-6">
                <div className="flex items-center gap-4 pb-4 border-b border-slate-200">
                  <div className="w-14 h-14 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Users size={28} className="text-emerald-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-slate-900">Table {selectedTable.name}</h3>
                    <p className="text-sm text-slate-600">How many guests are sitting at this table?</p>
                  </div>
                  <button onClick={() => setShowPaxModal(false)} className="p-2 hover:bg-slate-100 rounded-lg transition-colors">
                    <X size={20} className="text-slate-500" />
                  </button>
                </div>

                <div className="bg-slate-50 rounded-xl p-4 text-center">
                  <p className="text-sm text-slate-600 mb-1">Table Capacity</p>
                  <p className="text-3xl font-black text-slate-900">{selectedTable.capacity}</p>
                  <p className="text-xs text-slate-500 mt-1">Maximum Guests</p>
                </div>

                <div>
                  <p className="text-sm font-semibold text-slate-700 mb-3">Quick Select:</p>
                  <div className="grid grid-cols-4 gap-2">
                    {[1, 2, 3, 4, 5, 6, 7, 8].map(num => (
                      <button
                        key={num}
                        onClick={() => handleQuickPax(num)}
                        disabled={isPaxSubmitting}
                        className={`py-3 px-4 rounded-xl font-bold text-lg transition-all ${
                          isPaxSubmitting && paxInput === num.toString()
                            ? 'bg-emerald-600 text-white shadow-lg scale-105'
                            : paxInput === num.toString()
                              ? 'bg-emerald-600 text-white shadow-lg scale-105'
                              : 'bg-slate-100 text-slate-700 hover:bg-emerald-100 hover:text-emerald-700 hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed'
                        }`}
                      >
                        {isPaxSubmitting && paxInput === num.toString()
                          ? <Loader2 size={20} className="animate-spin mx-auto" />
                          : num}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">Or enter manually:</label>
                  <input
                    type="number"
                    min="1"
                    value={paxInput}
                    onChange={e => { setPaxInput(e.target.value); setPaxError(''); }}
                    onKeyPress={e => { if (e.key === 'Enter') handlePaxSubmit(); }}
                    placeholder="Enter number of guests"
                    className="w-full px-4 py-3 border-2 border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-lg font-semibold text-center"
                    autoFocus
                  />
                  {paxError && (
                    <p className="text-red-600 text-sm font-medium mt-2 flex items-center gap-2">
                      <span className="w-1.5 h-1.5 bg-red-600 rounded-full" />{paxError}
                    </p>
                  )}
                </div>

                <div className="flex gap-3 pt-2">
                  <button
                    onClick={() => setShowPaxModal(false)}
                    disabled={isPaxSubmitting}
                    className="flex-1 px-6 py-3 text-slate-600 rounded-xl font-semibold hover:bg-slate-100 transition-all border-2 border-slate-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handlePaxSubmit}
                    disabled={!paxInput || isPaxSubmitting}
                    className="flex-1 px-6 py-3 bg-emerald-600 text-white rounded-xl font-bold hover:bg-emerald-700 transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {isPaxSubmitting ? <><Loader2 size={18} className="animate-spin" />Creating...</> : 'Continue →'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ── Split Selection Modal ────────────────────────────────────── */}
          {showSplitSelectModal && selectedTable && (
            <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
              <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl p-6 space-y-6">
                <div className="flex items-center gap-4 pb-4 border-b border-slate-200">
                  <div className="w-14 h-14 bg-indigo-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Split size={28} className="text-indigo-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-slate-900">Table {selectedTable.name} — Multiple Orders</h3>
                    <p className="text-sm text-slate-600">
                      This table has {selectedSplits.length} separate orders. Select one to continue:
                    </p>
                  </div>
                  <button onClick={() => setShowSplitSelectModal(false)} className="p-2 hover:bg-slate-100 rounded-lg transition-colors">
                    <X size={20} className="text-slate-500" />
                  </button>
                </div>

                {/* Add new split button — only shown if canSplitOrder */}
                {canSplitOrder && (
                  <button
                    onClick={() => {
                      setShowSplitSelectModal(false);
                      setPaxInput('');
                      setPaxError('');
                      setShowPaxModal(true);
                    }}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2.5 border-2 border-dashed border-indigo-300 text-indigo-600 hover:bg-indigo-50 rounded-xl font-semibold text-sm transition"
                  >
                    <Split size={16} />
                    Add New Split
                  </button>
                )}

                <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-2">
                  {selectedSplits.map(order => {
                    const total = order.amount || 0;
                    const isHeld = ['HOLD', 'HELD', 'hold'].includes(order.status);
                    const isPaymentPending = order.status === 'payment_pending';

                    return (
                      <div
                        key={order.tableSplit}
                        className={`w-full text-left p-5 rounded-xl transition-all border-2 ${
                          isPaymentPending ? 'bg-pink-50 border-pink-500'
                            : isHeld ? 'bg-amber-50 border-amber-400'
                            : 'bg-indigo-50 border-indigo-400'
                        }`}
                      >
                        <button
                          onClick={() => handleSelectSplit(selectedTable.id, order.tableSplit, order.pax)}
                          className="w-full hover:scale-[1.02] hover:shadow-lg transition-all"
                        >
                          <div className="flex items-center justify-between gap-4">
                            <div className="flex items-center gap-4 flex-1">
                              <div className={`w-14 h-14 rounded-xl flex items-center justify-center font-black text-2xl text-white shadow-md ${
                                isPaymentPending ? 'bg-pink-500' : isHeld ? 'bg-amber-500' : 'bg-indigo-600'
                              }`}>
                                {order.tableSplit}
                              </div>
                              <div className="flex-1 space-y-2">
                                <div className="flex items-center gap-3">
                                  <p className="font-bold text-xl text-slate-900">Split {order.tableSplit || 'Main'}</p>
                                  {isPaymentPending && (
                                    <span className="text-xs font-bold bg-pink-600 text-white px-2 py-1 rounded-full uppercase flex items-center gap-1">
                                      <CreditCard size={12} />PAYMENT
                                    </span>
                                  )}
                                  {isHeld && !isPaymentPending && (
                                    <span className="text-xs font-bold bg-amber-600 text-white px-2 py-1 rounded-full uppercase">ON HOLD</span>
                                  )}
                                </div>
                                <div className="flex items-center gap-6 text-sm">
                                  <div className="flex items-center gap-2 text-slate-700">
                                    <div className={`p-1.5 rounded-lg ${isPaymentPending ? 'bg-pink-100' : isHeld ? 'bg-amber-100' : 'bg-indigo-100'}`}>
                                      <Users size={14} className={isPaymentPending ? 'text-pink-600' : isHeld ? 'text-amber-600' : 'text-indigo-600'} />
                                    </div>
                                    <span className="font-semibold">{order.pax || 0}</span>
                                    <span className="text-slate-500">Guests</span>
                                  </div>
                                  <div className="flex items-center gap-2 text-slate-700">
                                    <div className={`p-1.5 rounded-lg ${isPaymentPending ? 'bg-pink-100' : isHeld ? 'bg-amber-100' : 'bg-indigo-100'}`}>
                                      <Clock size={14} className={isPaymentPending ? 'text-pink-600' : isHeld ? 'text-amber-600' : 'text-indigo-600'} />
                                    </div>
                                    <span className="font-semibold">{formatRunningTime(order.createdAt)}</span>
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-4">
                              <div className="text-right">
                                <p className="text-xs text-slate-500 font-medium mb-1">Total Bill</p>
                                <div className="flex items-center gap-1">
                                  <IndianRupee size={24} className="text-slate-700" />
                                  <span className="text-3xl font-black text-slate-900">{total.toFixed(0)}</span>
                                </div>
                              </div>
                              <ArrowRight size={28} className={isPaymentPending ? 'text-pink-500' : isHeld ? 'text-amber-500' : 'text-indigo-500'} />
                            </div>
                          </div>
                        </button>

                        {/* Action buttons per split */}
                        <div className="flex gap-2 mt-4 pt-4 border-t border-slate-200">
                          <button
                            onClick={e => { e.stopPropagation(); handleViewOrders(selectedTable.id, order.tableSplit, selectedTable.name); }}
                            className="flex-1 px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white text-sm font-bold rounded-lg transition-colors flex items-center justify-center gap-2"
                          >
                            <Eye size={16} />View Orders
                          </button>

                          {isPaymentPending ? (
                            canSettlePerm && (
                              <button
                                onClick={e => { e.stopPropagation(); handleSettleBill(selectedTable.id, order.tableSplit, order.pax); }}
                                className="flex-1 px-4 py-2 bg-pink-600 hover:bg-pink-700 text-white text-sm font-bold rounded-lg transition-colors flex items-center justify-center gap-2"
                              >
                                <CreditCard size={16} />Settle Payment
                              </button>
                            )
                          ) : (
                            canPrintBillPerm && (
                              <button
                                onClick={e => { e.stopPropagation(); handlePrintBill(selectedTable.id, order.tableSplit, order.pax); }}
                                className="flex-1 px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white text-sm font-bold rounded-lg transition-colors flex items-center justify-center gap-2"
                              >
                                <Printer size={16} />Print Bill
                              </button>
                            )
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="pt-4 border-t border-slate-200">
                  <button
                    onClick={() => setShowSplitSelectModal(false)}
                    className="w-full px-6 py-3 text-slate-600 rounded-xl font-semibold hover:bg-slate-100 transition-all border-2 border-slate-200"
                  >
                    ← Back to Tables
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ── Bottom Action Dock ───────────────────────────────────────── */}
          <div className="bg-gray-900 px-6 py-2 flex items-center justify-center gap-2">
            <button
              onClick={() => navigate('/pos/browse', { state: { browseMode: true } })}
              className="flex items-center gap-2 px-4 py-1.5 rounded-lg text-slate-300 hover:bg-white/10 hover:text-white transition"
            >
              <Menu size={16} />
              <span className="text-xs font-medium">View Menu</span>
            </button>

            <div className="h-4 w-px bg-white/20" />

            <button
              onClick={() => setShowRecentOrdersModal(true)}
              className="flex items-center gap-2 px-4 py-1.5 rounded-lg text-slate-300 hover:bg-white/10 hover:text-white transition"
            >
              <History size={16} />
              <span className="text-xs font-medium">Recent Orders</span>
            </button>

            {canAccessWaitlist && (
              <>
                <div className="h-4 w-px bg-white/20" />
                <button
                  onClick={() => setShowWaitingListModal(true)}
                  className="relative flex items-center gap-2 px-4 py-1.5 rounded-lg text-slate-300 hover:bg-white/10 hover:text-white transition"
                >
                  <UserPlus size={16} />
                  <span className="text-xs font-medium">Waiting List</span>
                  {activeWaitingCount > 0 && (
                    <span className="ml-1 min-w-[18px] h-4 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1">
                      {activeWaitingCount}
                    </span>
                  )}
                </button>
              </>
            )}

            {canViewIncoming && (
              <>
                <div className="h-4 w-px bg-white/20" />
                <button
                  onClick={() => setShowIncomingOrdersModal(true)}
                  className="relative flex items-center gap-2 px-4 py-1.5 rounded-lg text-slate-300 hover:bg-white/10 hover:text-white transition"
                >
                  <Bell size={16} />
                  <span className="text-xs font-medium">Incoming Orders</span>
                  {qrPendingOrders.length > 0 && (
                    <span className="ml-1 min-w-[18px] h-4 bg-amber-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1 animate-pulse">
                      {qrPendingOrders.length}
                    </span>
                  )}
                </button>
              </>
            )}
          </div>
        </>
      )}

      {/* ── Modals (outside loading gate) ────────────────────────────────── */}

      {showViewOrdersModal && viewOrdersData && (
        <ViewOrdersModal
          tableId={viewOrdersData.tableId}
          tableSplit={viewOrdersData.tableSplit}
          tableName={viewOrdersData.tableName}
          onClose={() => { setShowViewOrdersModal(false); setViewOrdersData(null); }}
        />
      )}

      {showPrintReceiptModal && printReceiptData && (
        <PrintReceiptModal
          tableId={printReceiptData.tableId}
          tableSplit={printReceiptData.tableSplit}
          tableName={printReceiptData.tableName}
          pax={printReceiptData.pax}
          onClose={() => { setShowPrintReceiptModal(false); setPrintReceiptData(null); }}
          onSuccess={refreshTables}
          canApplyPromo={canApplyPromo}
          canApplyDiscount={canApplyDiscount}
        />
      )}

      {showRecentOrdersModal && (
        <RecentOrdersModal onClose={() => setShowRecentOrdersModal(false)} hideRepeatButton />
      )}

      <QuickReserveModal
        isOpen={showReserveModal}
        onClose={() => { setShowReserveModal(false); setReserveTable(null); }}
        table={reserveTable}
        onReserve={handleReservationSubmit}
      />

      {showWaitingListModal && (
        <WaitingListModal
          waitingList={waitingList}
          onAdd={canWaitlistAdd ? handleAddToWaitingList : null}
          onNotify={canWaitlistNotify ? handleNotifyCustomer : null}
          onRemove={canWaitlistRemove ? handleRemoveFromWaitingList : null}
          onSeated={handleCustomerSeated}
          onClose={() => setShowWaitingListModal(false)}
          availableTablesCount={stats.available}
        />
      )}

      {showMergeModal && mergeInitiatingTable && (
        <TableMergeModal
          tables={tables}
          initiatingTable={mergeInitiatingTable}
          mode={mergeMode}
          onMerge={handleMergeTables}
          onClose={() => { setShowMergeModal(false); setMergeInitiatingTable(null); }}
          isLoading={isMerging}
        />
      )}

      {showEndShiftModal && <EndShiftModal onClose={() => setShowEndShiftModal(false)} />}
      {showDayCloseModal && <DayCloseModal onClose={() => setShowDayCloseModal(false)} />}

      {canViewIncoming && showIncomingOrdersModal && (
        <IncomingOrdersModal
          onClose={() => setShowIncomingOrdersModal(false)}
          onOrderApproved={fetchTables}
          canSendKitchen={canSendKitchen}
          canPrintKOT={canPrintKOTIncoming}
        />
      )}
    </div>
  );
}